import { useMsal } from '@azure/msal-react';
import { TIntegrationType } from '@gen2/api/integrations/api';
import { useDisconnectIntMutation } from '@gen2/api/integrations/hooks';
import { useAuth } from '@gen2/hooks';
import { Microsoft } from '@gen2/services/social-login/microsoft/microsoft';
import { PermissionsKeys } from '@gen2/types/permissions';
import { generateStateString } from '@gen2/utils/auth';
import { usePermissions } from '@gen2/utils/permissions/permissions';
import { useGoogleLogin } from '@react-oauth/google';
import { noop } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntegrationsStore } from './store';

export type TIntegrationAvailability = {
  google: boolean;
  microsoft: boolean;
  box: boolean;
  box_account: boolean;
  sharepoint: boolean;
  google_account: boolean;
  hubspot: boolean;
  salesforce: boolean;
};

export type TIntegrationStates = {
  disabled: TIntegrationAvailability;
};

export interface IIntegrationContext {
  isLoading: boolean;
  isConnected: (v: TIntegrationType) => boolean;
  disabled: TIntegrationAvailability;
  connect: (type: TIntegrationType) => void;
  disconnect: (type: TIntegrationType) => void;
  disconnectAsync: () => Promise<boolean>;
}

const IntegrationContext = createContext<IIntegrationContext>({
  isLoading: false,
  isConnected: () => false,
  disabled: {
    google: false,
    microsoft: false,
    box: false,
    sharepoint: false,
    google_account: false,
    box_account: false,
    hubspot: false,
    salesforce: false,
  },
  connect: noop,
  disconnect: noop,
  disconnectAsync: async () => false,
});

export type IntegrationProviderProps = {
  children: React.ReactNode;
};

export const IntegrationProvider = ({ children }: IntegrationProviderProps) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const { vendor } = useParams<{ vendor: TIntegrationType }>();
  const stateString = generateStateString();
  const redirectUri = useCallback((type: string) => {
    return `${window.location.origin}/auth/integration/${type}/callback`;
  }, []);
  const { setConnectConfirmOpen } = useIntegrationsStore();
  const { withPermissions } = usePermissions();

  const integrationStates = useMemo<TIntegrationStates>(() => {
    // Helper function to check if any account-level integration is connected
    const getConnectedAccountIntegration = () => {
      const accountIntegrations =
        user?.organisations[0].accounts[0]?.integrations ?? {};
      return Object.entries(accountIntegrations).find(
        ([_, integration]) =>
          (integration as { is_connected: boolean }).is_connected,
      )?.[0];
    };

    const connectedAccountIntegration = getConnectedAccountIntegration();

    // Start with all integrations enabled
    const disabled = {
      google: false,
      microsoft: false,
      sharepoint: false,
      box: false,
      box_account: false,
      google_account: false,
      hubspot: false,
      salesforce: false,
    };

    // If any account-level integration is connected, disable all user-level integrations
    if (connectedAccountIntegration) {
      disabled.google = true;
      disabled.microsoft = true;
      disabled.box = true;
    }

    // Apply permission-based disabling
    const permissionChecks = [
      {
        permission: PermissionsKeys.CREATE_GOOGLE_DRIVE_INTEGRATION,
        key: 'google',
      },
      {
        permission: PermissionsKeys.CREATE_ONEDRIVE_BUSINESS_INTEGRATION,
        key: 'microsoft',
      },
      { permission: PermissionsKeys.CREATE_BOX_INTEGRATION, key: 'box' },
      {
        permission: PermissionsKeys.CREATE_GOOGLE_DRIVE_ACCOUNT_INTEGRATION,
        key: 'google_account',
      },
      {
        permission: PermissionsKeys.CREATE_BOX_ACCOUNT_INTEGRATION,
        key: 'box_account',
      },
      {
        permission: PermissionsKeys.CREATE_SHAREPOINT_INTEGRATION,
        key: 'sharepoint',
      },
      {
        permission: PermissionsKeys.GET_EMBEDDED_HUBSPOT_LINK,
        key: 'hubspot',
      },
      {
        permission: PermissionsKeys.GET_EMBEDDED_SALESFORCE_LINK,
        key: 'salesforce',
      },
    ];

    permissionChecks.forEach(({ permission, key }) => {
      if (!withPermissions(permission)) {
        disabled[key as keyof TIntegrationAvailability] = true;
      }
    });

    return {
      disabled,
    };
  }, [user, withPermissions]);

  const googleConnectScope =
    'email openid https://www.googleapis.com/auth/drive.file';

  const googleAccountConnectScope =
    googleConnectScope + ' https://www.googleapis.com/auth/drive.readonly';

  const googleConnect = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: redirectUri('google'),
    select_account: true,
    state: stateString,
    enable_serial_consent: false,
    overrideScope: true,
    scope: googleConnectScope,
  });

  const driveConnect = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: redirectUri('google_account'),
    select_account: true,
    state: stateString,
    enable_serial_consent: false,
    overrideScope: true,
    scope: googleAccountConnectScope,
  });

  const { instance: msInstance } = useMsal();
  const navigate = useNavigate();

  const microsoftConnect = () => {
    Microsoft.authOnedriveRedirect(msInstance, navigate);
  };

  const boxConnect = () => {
    const baseUrl = 'https://account.box.com/api/oauth2/authorize';
    const clientId = process.env['NX_PUBLIC_BOX_CLIENT_ID'];
    const redirectUri = `${window.location.origin}/auth/integration/box/callback`;
    const redirectParam = redirectUri ? `&redirect_uri=${redirectUri}` : '';
    window.location.href = `${baseUrl}?client_id=${clientId}&response_type=code${redirectParam}`;
  };

  const sharepointConnect = async () => {
    const isConfirmed = await setConnectConfirmOpen('SharePoint');

    setIsLoading(false);

    if (!isConfirmed) return;

    Microsoft.authSharepointRedirect(msInstance, navigate);
  };

  const googleAccountConnect = async () => {
    const isConfirmed = await setConnectConfirmOpen('Google Drive');

    setIsLoading(false);

    if (!isConfirmed) return;

    driveConnect();
  };

  const boxAccountConnect = async () => {
    const isConfirmed = await setConnectConfirmOpen('Box');

    setIsLoading(false);

    if (!isConfirmed) return;

    const baseUrl = 'https://account.box.com/api/oauth2/authorize';
    const clientId = process.env['NX_PUBLIC_BOX_CLIENT_ID'];
    const redirectUri = `${window.location.origin}/auth/integration/box_account/callback`;
    const redirectParam = redirectUri ? `&redirect_uri=${redirectUri}` : '';
    window.location.href = `${baseUrl}?client_id=${clientId}&response_type=code${redirectParam}`;
  };

  const hubspotConnect = async () => {
    const isConfirmed = await setConnectConfirmOpen('HubSpot');

    setIsLoading(false);

    if (!isConfirmed) return;

    navigate('/integrations/hubspot');
  };

  const {
    mutate: disconnectStorageMutation,
    mutateAsync: disconnectStorageMutationAsync,
  } = useDisconnectIntMutation();

  const { initialize } = useAuth();
  const { setAlert } = useIntegrationsStore();

  const isConnected = useCallback(
    (v: TIntegrationType) => {
      switch (v) {
        case 'google':
          return !!user?.integrations?.drive?.is_connected;
        case 'microsoft':
          return !!user?.integrations?.onedrive?.is_connected;
        case 'box':
          return !!user?.integrations?.box?.is_connected;
        case 'sharepoint':
          return !!user?.organisations[0].accounts[0]?.integrations?.sharepoint
            ?.is_connected;
        case 'google_account':
          return !!user?.organisations[0].accounts[0]?.integrations
            ?.google_account?.is_connected;
        case 'box_account':
          return !!user?.organisations[0].accounts[0]?.integrations?.box_account
            ?.is_connected;
        case 'hubspot':
          return !!user?.organisations[0].accounts[0]?.integrations?.hubspot
            ?.is_connected;
        default:
          return false;
      }
    },
    [user],
  );

  const connect = (type: TIntegrationType) => {
    setIsLoading(true);

    switch (type) {
      case 'google':
        googleConnect();
        break;
      case 'microsoft':
        microsoftConnect();
        break;
      case 'box':
        boxConnect();
        break;
      case 'sharepoint':
        sharepointConnect();
        break;
      case 'google_account':
        googleAccountConnect();
        break;
      case 'box_account':
        boxAccountConnect();
        break;
      case 'hubspot':
        hubspotConnect();
        break;
      default:
        break;
    }
  };

  const disconnect = (type: TIntegrationType) => {
    setIsLoading(true);

    const typeMessage = {
      google: 'Google Drive',
      google_account: 'Google Drive Account',
      microsoft: 'OneDrive',
      sharepoint: 'SharePoint',
      box: 'Box',
      box_account: 'Box Account',
      hubspot: 'HubSpot',
      salesforce: 'Salesforce',
    }[type];

    disconnectStorageMutation(
      { type },
      {
        onSuccess: () => {
          setAlert({
            message: `${typeMessage} was successfully disconnected!`,
            severity: 'warning',
            open: true,
          });
          initialize();
        },
        onError: () => {
          setAlert({
            message: `Failed to disconnect ${typeMessage}. Please try again.`,
            severity: 'error',
            open: true,
          });
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  };

  const disconnectAsync = async () => {
    const typeMessage = {
      google: 'Google Drive',
      google_account: 'Google Drive Account',
      microsoft: 'OneDrive',
      sharepoint: 'SharePoint',
      box: 'Box',
      box_account: 'Box Account',
      hubspot: 'HubSpot',
      salesforce: 'Salesforce',
    }[vendor || 'google'];

    try {
      await disconnectStorageMutationAsync({
        type: vendor || 'google',
      });

      setAlert({
        message: `${typeMessage} was successfully disconnected!`,
        severity: 'warning',
        open: true,
      });
      initialize();
      return true;
    } catch {
      // Error is already handled by the onError callback in disconnectStorageMutation
      return false;
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      isConnected,
      ...integrationStates,
      connect,
      disconnect,
      disconnectAsync,
    }),
    [
      isLoading,
      isConnected,
      integrationStates,
      connect,
      disconnect,
      disconnectAsync,
    ],
  );

  return (
    <IntegrationContext.Provider value={contextValue}>
      {children}
    </IntegrationContext.Provider>
  );
};

export const useIntegrationContext = () => {
  return useContext(IntegrationContext);
};
